import '@sophocles/components/BasicDashboardCard/styles.scss';

import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Skeleton, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import React, { Fragment, useContext, useEffect, useState } from 'react';

import BasicDashboardCardRail from '@sophocles/components/BasicDashboardCard/BasicDashboardCardRail';
import DashboardCardVariantContext, {
  DASHBOARD_CARD_VARIANT,
} from '@sophocles/components/BasicDashboardCard/dashboardCardVariant';
import { dashboardStatusColor } from '@sophocles/components/BasicDashboardCard/DashboardStatusDot';
import { hexToRgb } from '@sophocles/utils/color';

function BasicDashboardCard(props) {
  const { loading, loadingHeight, children, menuItems, actions, title, subtitle, status, toolbarExtras, variant } =
    props;

  const theme = useTheme();
  const contextVariant = useContext(DashboardCardVariantContext);
  const isRail = (variant ?? contextVariant) === DASHBOARD_CARD_VARIANT.RAIL;

  const [anchorEl, setAnchorEl] = useState(null);
  const [toolbarColor, setToolbarColor] = useState(dashboardStatusColor(status, theme.palette.primary.main));
  const open = Boolean(anchorEl);

  const handleClick = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    let defaultColor = theme.palette.primary.main;
    const defaultRgb = hexToRgb(defaultColor);
    if (defaultRgb.g > defaultRgb.r && defaultRgb.g > defaultRgb.b) {
      defaultColor = theme.palette.secondary.main;
    }
    setToolbarColor(dashboardStatusColor(status, defaultColor));
  }, [status]);

  if (loading) {
    return (
      <Skeleton variant="rectangular" height={loadingHeight ?? (isRail ? 140 : 420)} sx={{ mb: isRail ? 0 : 2 }} />
    );
  }

  // Rail variant: a flat sidebar section (see BasicDashboardCardRail). The
  // context-driven variant switch stays here; the layout lives in that component.
  if (isRail) {
    return (
      <BasicDashboardCardRail
        title={title}
        subtitle={subtitle}
        dotColor={dashboardStatusColor(status, null)}
        toolbarExtras={toolbarExtras}
        menuItems={menuItems}
        actions={actions}
        anchorEl={anchorEl}
        open={open}
        onMenuClick={handleClick}
        onMenuClose={handleClose}
      >
        {children}
      </BasicDashboardCardRail>
    );
  }

  return (
    <Card sx={{ mb: 2, width: '100%', borderWidth: '2px', overflow: 'hidden' }}>
      <Toolbar
        variant="dense"
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          backgroundColor: toolbarColor,
          minHeight: 40,
          px: 2,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{ color: 'common.white', fontWeight: 600, fontSize: '0.875rem', letterSpacing: '0.01em' }}
        >
          {title}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {toolbarExtras}
          {menuItems ? (
            <Fragment>
              <IconButton
                size="small"
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
                onClick={handleClick}
              >
                <MoreVertIcon sx={{ color: 'common.white', fontSize: 18 }} />
              </IconButton>
              <Menu anchorEl={anchorEl} open={open} onClick={handleClose} onClose={handleClose}>
                {menuItems}
              </Menu>
            </Fragment>
          ) : null}
        </Box>
      </Toolbar>
      {children}
      {actions ? <CardActions>{actions}</CardActions> : null}
    </Card>
  );
}

export default BasicDashboardCard;
